/**
 * Table columns for the Deleted companies tab (SF-4813): "In progress" and "History".
 */
import { Space, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';

import {
  attemptsLabel,
  daysLeftLabel,
  deletionErrors,
  formatDeletionDate,
  type CompanyDeletionRow,
  type DeletionActor,
} from '../api/companyDeleteClient';
import { canEditCompanyId, type EditableCompanies } from '../api/editableCompaniesClient';
import {
  DeletionActionButton,
  DeletionStatusTag,
  type DeletionAction,
} from './DeletionActionButton';

const { Text } = Typography;

/** Longest error text shown in the table; the full list is in the tooltip. */
const SHORT_ERROR_LENGTH = 80;

const companyName = (row: CompanyDeletionRow) => row.company_name || 'Unnamed company';

/**
 * A link only while the company is certainly still there: a scheduled row in In
 * progress. Once a purge has started the record may be half gone, and a History
 * row (even a cancelled one) proves nothing: the company may have been deleted
 * by a later request. So History names are always plain text.
 */
function companyCell(row: CompanyDeletionRow, mayLink: boolean) {
  const linkable = mayLink && row.status === 'scheduled';
  return linkable ? (
    <Link to={`/companies/${row.company_id}`}>{companyName(row)}</Link>
  ) : (
    <Text>{companyName(row)}</Text>
  );
}

function actorCell(actor?: DeletionActor | null) {
  if (!actor?.name && !actor?.email) return <Text type="secondary">Not recorded</Text>;
  return (
    <Space direction="vertical" size={0}>
      <Text>{actor.name || actor.email}</Text>
      {actor.name && actor.email ? <Text type="secondary">{actor.email}</Text> : null}
    </Space>
  );
}

/** Who asked, plus who forced it to run now and when (SF-5057), when someone did. */
function requestedByCell(row: CompanyDeletionRow) {
  if (!row.forced_at) return actorCell(row.requested_by);
  const forcer = row.forced_by?.name || row.forced_by?.email;
  const when = formatDeletionDate(row.forced_at);
  return (
    <Space direction="vertical" size={0}>
      {actorCell(row.requested_by)}
      <Text type="secondary">
        {forcer ? `Forced by ${forcer} on ${when}` : `Forced on ${when}`}
      </Text>
    </Space>
  );
}

function lastErrorCell(row: CompanyDeletionRow) {
  const errors = deletionErrors(row);
  if (errors.length === 0) return <Text type="secondary">None</Text>;
  const last = errors[errors.length - 1];
  const short = last.length > SHORT_ERROR_LENGTH ? `${last.slice(0, SHORT_ERROR_LENGTH)}...` : last;
  return (
    <Tooltip title={<div style={{ whiteSpace: 'pre-wrap' }}>{errors.join('\n')}</div>}>
      <Text type="danger">{short}</Text>
    </Tooltip>
  );
}

/** SF-5139: steps the purge left for a person (e.g. an outside account it could not reach). */
function manualStepsNote(row: CompanyDeletionRow) {
  const steps = row.manual_steps ?? [];
  if (steps.length === 0) return null;
  return (
    <Space direction="vertical" size={0}>
      <Text type="warning">To do by hand:</Text>
      {steps.map((step, i) => (
        <Text key={i} type="warning">
          {step}
        </Text>
      ))}
    </Space>
  );
}

export interface ActiveColumnsOptions {
  /** Deletion id whose action is being saved (its buttons show busy). */
  busyId: string | null;
  onAction: (row: CompanyDeletionRow, action: DeletionAction) => void;
  /**
   * Which companies the person may change (SF-4668), or null while that is
   * loading or could not be read. Null hides every action (fail closed).
   */
  editScope: EditableCompanies | null;
  now?: number;
}

/** The row's Cancel / Retry: both the row's own flag and the person's company list. */
function actionsCell(
  row: CompanyDeletionRow,
  { busyId, onAction, editScope }: ActiveColumnsOptions,
) {
  if (!row.can_cancel && !row.can_retry) return null;
  if (!editScope) return null;
  if (!canEditCompanyId(editScope, row.company_id)) {
    return <Text type="secondary">Not on your company list</Text>;
  }
  const button = (action: DeletionAction) => (
    <DeletionActionButton
      action={action}
      size="small"
      companyName={row.company_name}
      loading={busyId === row._id}
      disabled={busyId !== null && busyId !== row._id}
      onConfirm={() => onAction(row, action)}
    />
  );
  return (
    <Space>
      {row.can_cancel ? button('cancel') : null}
      {row.can_retry ? button('retry') : null}
    </Space>
  );
}

export function activeDeletionColumns(
  options: ActiveColumnsOptions,
): ColumnsType<CompanyDeletionRow> {
  const { now } = options;
  return [
    { key: 'company', title: 'Company', render: (_, row) => companyCell(row, true) },
    {
      key: 'status',
      title: 'Status',
      render: (_, row) => <DeletionStatusTag status={row.status} />,
    },
    {
      key: 'delete_after',
      title: 'Deletes from',
      render: (_, row) =>
        row.delete_after ? (
          <Space direction="vertical" size={0}>
            <Text>{formatDeletionDate(row.delete_after)}</Text>
            {row.status === 'scheduled' ? (
              <Text type="secondary">
                {row.forced_at ? 'starting now' : daysLeftLabel(row.delete_after, now)}
              </Text>
            ) : null}
          </Space>
        ) : (
          '—'
        ),
    },
    {
      key: 'requested_by',
      title: 'Requested by',
      render: (_, row) => requestedByCell(row),
    },
    {
      key: 'attempts',
      title: 'Attempts',
      render: (_, row) =>
        row.attempts ? (
          <Space direction="vertical" size={0}>
            <Text>{attemptsLabel(row.attempts)}</Text>
            {row.auto_retry_pending ? (
              <Text type="secondary">Will retry on the next nightly run</Text>
            ) : null}
          </Space>
        ) : (
          '—'
        ),
    },
    {
      key: 'error',
      title: 'Last error',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          {lastErrorCell(row)}
          {manualStepsNote(row)}
        </Space>
      ),
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => actionsCell(row, options),
    },
  ];
}

/** Finished on: when it was deleted, or when it was cancelled. */
const finishedOn = (row: CompanyDeletionRow) =>
  row.status === 'cancelled' ? (row.cancelled_at ?? row.finished_at) : row.finished_at;

export function historyDeletionColumns(): ColumnsType<CompanyDeletionRow> {
  return [
    { key: 'company', title: 'Company', render: (_, row) => companyCell(row, false) },
    {
      key: 'outcome',
      title: 'Outcome',
      // A cancelled row can carry the reason (the nightly job closes a record whose
      // company is no longer marked for deletion), so show it under the tag.
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <DeletionStatusTag status={row.status} />
          {deletionErrors(row).length > 0 ? lastErrorCell(row) : null}
          {manualStepsNote(row)}
        </Space>
      ),
    },
    {
      key: 'requested_by',
      title: 'Requested by',
      render: (_, row) => requestedByCell(row),
    },
    {
      key: 'requested_at',
      title: 'Requested on',
      render: (_, row) => formatDeletionDate(row.requested_at) || '—',
    },
    {
      key: 'finished',
      title: 'Finished on',
      render: (_, row) => formatDeletionDate(finishedOn(row)) || '—',
    },
    {
      key: 'records',
      title: 'Records removed',
      // Backfilled and cancelled rows carry no counts.
      render: (_, row) =>
        typeof row.result?.total_deleted === 'number'
          ? row.result.total_deleted.toLocaleString()
          : '—',
    },
  ];
}
